import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  combineLatest,
  concatMap,
  debounce,
  filter,
  first,
  forkJoin,
  groupBy,
  last,
  map,
  merge,
  Observable,
  of,
  pairwise,
  scan,
} from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { CanvasElement, CanvasElementId, RenderTarget } from '@blaze-runtime/engine-domain';
import {
  CanvasElementInputBinding,
  DataBindingSourceInfo,
  FormContext,
} from '@blaze-shared/elements-domain';
import { countedShareReplay, isNullish } from '@blaze-shared/shared-util';
import { WorkflowPhase, WorkflowStatus } from '@blaze-shared/workflow-domain';

import { EngineState } from '../+state/engine';
import { InputDataPackage } from '../canvas-elements/io-data/io-data.models';

import { ElementStoreViewService } from './element-store-view.service';
import { ViewportService } from './viewport.service';

export interface CanvasElementInputData {
  valueResult: InputDataPackage;
  isPaginatedOnServer?: boolean;
  inputName: CanvasElementInputBinding;
  dataAdapter: (data: any, formContext?: FormContext) => any;
  dataSource: DataBindingSourceInfo;
}

@Injectable({ providedIn: 'root' })
export class RenderElementService {
  private readonly store = inject<Store<EngineState>>(Store);
  private readonly elementStoreView = inject(ElementStoreViewService);
  private readonly viewportService = inject(ViewportService);

  /** Shared element dictionary — one store subscription for the whole render tree. */
  readonly entities$ = this.elementStoreView.entities$;
  readonly entityState$ = this.elementStoreView.entityState$;

  readonly renderTarget$: Observable<RenderTarget>;
  readonly navigationStarted$: Observable<string>;

  constructor() {
    this.navigationStarted$ = this.store.select(runtimeSelectors.selectPageId).pipe(
      pairwise(),
      filter(([prev, curr]) => {
        if (isNullish(prev) || isNullish(curr)) {
          return false;
        }
        return prev !== curr;
      }),
      map(([, curr]) => curr),
    );

    this.renderTarget$ = this.viewportService.mode$.pipe(
      map((mode) => mode ?? 'desktop'),
      countedShareReplay(),
    );
  }

  getElement(elementId: CanvasElementId): Observable<CanvasElement | undefined> {
    return this.elementStoreView.getElement(elementId);
  }

  /**
   * The handler waits for all (non-blocking) inputs to be ready (have the data) before emitting the data to the element.
   * @param blockingInputs list of input names that need to resolve before the data can be passed to the component
   */
  createInputStreamsHandler(
    inputStreams$: Observable<CanvasElementInputData>[],
    blockingInputs: string[],
  ): Observable<CanvasElementInputData[]> {
    const inputCount = inputStreams$.length;

    // Single input can be passed directly as it doesn't need input grouping during initial load and debouncing when the data is loading
    if (inputCount === 1) {
      return combineLatest(inputStreams$);
    }

    return merge(...inputStreams$).pipe(
      // Collect data for all inputs and keep track of the current input to be used further in the pipeline
      scan(
        (acc, input) => {
          const allInputs = { ...acc.allInputs, [input.inputName]: input };
          return {
            ...acc,
            allInputs,
            current: input.inputName,
            ready: blockingInputs.every((key) => allInputs[key]), // Check if all blocking inputs are ready
          };
        },
        {
          allInputs: {} as Record<string, CanvasElementInputData>,
          current: undefined as CanvasElementInputBinding | undefined, // currently handled input
          ready: false, // are all bound inputs ready
        },
      ),
      // Don't do anything until all inputs are ready
      filter(({ ready }) => ready),
      map((inputsUpdateData, index) => ({
        ...inputsUpdateData,
        isInitial: index === 0,
      })),
      // The code below groups all of the input updates coming from above into a new stream which is handled further in the pipeline.
      // That stream lasts for as long as the `duration` provided - if any of the input sources use a workflow,
      // the emissions of the `inputsUpdateData` will be paused until the workflow successfully finishes its execution
      // and all of the updates which took part while waiting will be grouped together and sent further as one array.
      // If there are no workflows, the group is closed instantly with the update information for just one piece of data emitted.
      groupBy(() => '', {
        duration: (group$) =>
          group$.pipe(
            debounce(({ allInputs }) => {
              // Wait for all inputs to be ready
              return forkJoin(
                Object.values(allInputs).map(({ dataSource, inputName }) => {
                  const isNonBlockingInput = !blockingInputs.includes(inputName);
                  const blockingInputFilter = (output: any, status: WorkflowStatus): boolean =>
                    !!output && (!status?.phase || status.phase === WorkflowPhase.BlockedWithData);
                  const nonBlockingInputFilter = (output: any, status: WorkflowStatus): boolean =>
                    status?.phase === WorkflowPhase.Blocked || blockingInputFilter(output, status);
                  if (dataSource.sourceType !== 'context') {
                    // If not a workflow -> assume the data is ready instantly
                    return of(true);
                  }
                  // In case of a workflow check or wait for the workflow to be ready
                  return this.store
                    .select(
                      runtimeSelectors.selectWorkflowsSchemaWithOutputAndStatusById(dataSource.id),
                    )
                    .pipe(
                      first(({ output, status }) =>
                        isNonBlockingInput
                          ? nonBlockingInputFilter(output, status)
                          : blockingInputFilter(output, status),
                      ),
                    );
                }),
              );
            }),
          ),
      }),
      // Handling group - combining the input updates coming while the workflow is in progress
      concatMap((group$) =>
        group$.pipe(
          // Gather and group recent input changes which happened while the values were debounced by possibly waiting for the workflow to finish
          scan(
            (acc, { allInputs, current, isInitial }) => {
              // Return all inputs for the first emission
              if (isInitial) {
                return allInputs;
              }
              // If current missing - don't react
              if (!current) {
                return acc;
              }

              // Update the recent input changes info
              return { ...acc, [current]: allInputs[current] };
            },
            {} as Record<string, CanvasElementInputData>,
          ),
          last(),
          // Map dictionary to an array
          map((inputChanges) => Object.values(inputChanges)),
        ),
      ),
    );
  }
}
